import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import LegacyIncidentQueryRedirect, {
  IncidentTabRedirect,
} from './components/projects/operate/LegacyIncidentQueryRedirect';
import OperateTabAliasRedirect from './components/projects/operate/OperateTabAliasRedirect';
import {
  CertificationPage,
  ExecutiveBriefingPage,
  ExecutorCenterPage,
  ExecutionsPage,
  LegacyOperateRedirect,
  IncidentsDomainPage,
  RuntimeLogsDomainPage,
  ReportedIncidentsPage,
  EnterpriseBusinessImpactPage,
  EnterpriseDependenciesPage,
  EnterpriseExecutivePage,
  EnterpriseLayoutPage,
  EnterprisePortfolioPage,
  EnterprisePriorityPage,
  EnterpriseStrategyPage,
  GovernanceApprovalHistoryPage,
  GovernanceCalendarPage,
  GovernanceLayoutPage,
  GovernancePendingApprovalsPage,
  GovernancePoliciesPage,
  GovernanceTeamPage,
  InsightsPage,
  KnowledgeLibraryPage,
  OperateFixesPage,
  OperateLayoutPage,
  PortfolioPage,
  ReleasesPage,
  OperateLearningPage,
  OperateRequestsPage,
  OperateTestEnvironmentPage,
  OverviewPage,
  Phase0ArtifactsPage,
  Phase0BaselinePage,
  Phase0ChangesPage,
  Phase0HandoffPage,
  Phase0LayoutPage,
  Phase0ReportPage,
  Phase0RepoPage,
  Phase0RepositoryPage,
  Phase0ReviewPage,
  Phase0UploadPage,
  Phase1GraphEnginePage,
  Phase1GraphExplorerPage,
  Phase1GraphPage,
  Phase1InsightsPage,
  Phase1KnowledgePage,
  Phase1LayoutPage,
  ProjectHomePage,
  ProjectIngestionPage,
  ProjectLegacyRedirect,
  ProjectWorkspacePage,
  ProjectsPage,
  SettingsPage,
  MonitoringPage,
  ScheduledJobsPage,
  KnowledgePage,
  PlatformInsightsPage,
} from './routes/lazyPages';
import {
  ProjectLegacyArtifactsPage,
  ProjectLegacyHandoffPage,
  ProjectLegacyIntakePage,
  ProjectLegacyReportPage,
  ProjectLegacyScanPage,
} from './pages/AppPages';
import { lightTheme, darkTheme } from './theme/theme';
import { useAppStore } from './store/appStore';
import { AuthProvider } from './auth/AuthContext';
import RequireAuth from './components/auth/RequireAuth';
import { ErrorBoundary } from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import InvitePage from './pages/InvitePage';
import { isAuthError } from './lib/apiError';

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchIntervalInBackground: false,
        // Never retry an auth failure: the session is gone (401) or the caller
        // is not allowed (403) — retrying only delays the redirect to /login.
        retry: (failureCount, error) => !isAuthError(error) && failureCount < 1,
        staleTime: 30_000,
        gcTime: 5 * 60_000,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

const queryClient = createAppQueryClient();

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

const App: React.FC = () => {
  const { mode } = useAppStore();
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Track the OS preference so "system" mode flips the MUI theme *and* the CSS
  // variables together (previously only the CSS variables followed it).
  useEffect(() => {
    if (mode !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  const resolvedMode: 'light' | 'dark' = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  const theme = useMemo(() => (resolvedMode === 'dark' ? darkTheme : lightTheme), [resolvedMode]);

  // Layout effect: set the CSS-variable mode before paint so MUI surfaces and
  // var(--…) surfaces never render one frame in different modes.
  useLayoutEffect(() => {
    document.documentElement.dataset.colorMode = resolvedMode;
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter
          future={{
            v7_startTransition: false,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            {/* Route-level boundary: a render error in one page no longer
                unmounts the whole application to the root boundary. */}
            <ErrorBoundary region="page">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* Outside RequireAuth: its login redirect would drop the #token
                  fragment. The page gates on sign-in itself. */}
              <Route path="/invite" element={<InvitePage />} />
              <Route element={<RequireAuth />}>
                <Route path="/" element={<Navigate to="/overview" replace />} />
                <Route path="/dashboard" element={<Navigate to="/overview" replace />} />

                <Route path="/overview" element={<OverviewPage />} />
                <Route path="/mission-control" element={<LegacyOperateRedirect tab="incidents" />} />
                <Route path="/investigations" element={<LegacyOperateRedirect tab="incidents" />} />
                <Route path="/executions" element={<LegacyOperateRedirect tab="executions" />} />
                <Route path="/certification" element={<LegacyOperateRedirect tab="replay" />} />
                <Route path="/executors" element={<LegacyOperateRedirect tab="executors" />} />
                <Route path="/monitoring" element={<MonitoringPage />} />
                <Route path="/monitoring/jobs" element={<ScheduledJobsPage />} />
                {/* The worker fleet page was replaced by workspace monitoring. */}
                <Route path="/workers" element={<Navigate to="/monitoring" replace />} />
                <Route path="/knowledge" element={<KnowledgePage />} />
                <Route path="/engineering" element={<LegacyOperateRedirect tab="fixes" />} />
                <Route path="/executive" element={<ExecutiveBriefingPage />} />
                <Route path="/analytics" element={<PlatformInsightsPage />} />
                {/* /swarm, /integrations and /projects/templates were placeholder
                    pages with no backend; they now fall through to the catch-all. */}

                <Route path="/projects" element={<Navigate to="/projects/all" replace />} />
                <Route path="/projects/:projectId" element={<ProjectWorkspacePage />}>
                  <Route index element={<ProjectHomePage />} />

                  {/* Discover — Phase 0 */}
                  <Route path="phase-0" element={<Phase0LayoutPage />}>
                    <Route index element={<Navigate to="repository" replace />} />
                    <Route path="repository" element={<Phase0RepositoryPage />} />
                    <Route path="artifacts" element={<Phase0ArtifactsPage />} />
                    <Route path="changes" element={<Phase0ChangesPage />} />
                    <Route path="review" element={<Phase0ReviewPage />} />
                    {/* Legacy: Dependencies & Knowledge removed from Discover */}
                    <Route path="dependencies" element={<Navigate to="../artifacts" replace />} />
                    <Route path="knowledge" element={<Navigate to="../../phase-1/graph" replace />} />
                    <Route path="knowledge/explorer" element={<Navigate to="../../phase-1/graph/explorer" replace />} />
                    {/* Legacy aliases */}
                    <Route path="upload" element={<Phase0UploadPage />} />
                    <Route path="repo" element={<Phase0RepoPage />} />
                    <Route path="graph" element={<Navigate to="../artifacts" replace />} />
                    <Route path="graph/explorer" element={<Navigate to="../../phase-1/graph/explorer" replace />} />
                    <Route path="baseline" element={<Phase0BaselinePage />} />
                    <Route path="verify" element={<Navigate to="../repository" replace />} />
                    <Route path="workflow" element={<Navigate to="../repository" replace />} />
                    <Route path="overview" element={<Navigate to="../artifacts" replace />} />
                    <Route path="delta" element={<Navigate to="../changes" replace />} />
                    <Route path="inventory" element={<Navigate to="../artifacts" replace />} />
                    <Route path="report" element={<Navigate to="../review" replace />} />
                    <Route path="handoff" element={<Navigate to="../review" replace />} />
                    <Route path="intake" element={<ProjectLegacyIntakePage />} />
                    <Route path="scan" element={<ProjectLegacyScanPage />} />
                  </Route>

                  {/* Understand — Phase 1 */}
                  <Route path="phase-1" element={<Phase1LayoutPage />}>
                    <Route index element={<Navigate to="insights" replace />} />
                    <Route path="insights" element={<Phase1InsightsPage />} />
                    <Route path="graph" element={<Phase1GraphPage />} />
                    <Route path="graph/explorer" element={<Phase1GraphExplorerPage />} />
                    {/* KG engine views (file / domain / flow / dependency) — /projects/:id/kg/* backend */}
                    <Route path="graph/engine" element={<Phase1GraphEnginePage />} />
                    <Route path="graph/engine/:view" element={<Phase1GraphEnginePage />} />
                    <Route path="knowledge" element={<Phase1KnowledgePage />} />
                    <Route path="ask" element={<Navigate to="../knowledge" replace />} />
                    <Route path="layers" element={<Navigate to="../insights" replace />} />
                    <Route path="layers/*" element={<Navigate to="../insights" replace />} />
                    <Route path="structural" element={<Navigate to="../insights" replace />} />
                    <Route path="behavioral" element={<Navigate to="../graph" replace />} />
                    <Route path="security" element={<Navigate to="../insights" replace />} />
                    <Route path="business" element={<Navigate to="../insights" replace />} />
                  </Route>

                  {/* Legacy Change → Operate */}
                  <Route path="phase-2">
                    <Route index element={<Navigate to="../operate/incidents" replace />} />
                    <Route path="*" element={<Navigate to="../../operate/engineering" replace />} />
                  </Route>
                  {/* P1 Milestone 1 — Governance (Policies / Pending Approvals) */}
                  <Route path="governance" element={<GovernanceLayoutPage />}>
                    <Route index element={<Navigate to="policies" replace />} />
                    <Route path="policies" element={<GovernancePoliciesPage />} />
                    <Route path="pending-approvals" element={<GovernancePendingApprovalsPage />} />
                    <Route path="calendar" element={<GovernanceCalendarPage />} />
                    <Route path="history" element={<GovernanceApprovalHistoryPage />} />
                    <Route path="team" element={<GovernanceTeamPage />} />
                  </Route>
                  {/* P1 M5-M8 */}
                  <Route path="releases" element={<ReleasesPage />} />
                  <Route path="insights" element={<InsightsPage />} />
                  <Route path="portfolio" element={<PortfolioPage />} />
                  <Route path="knowledge-library" element={<KnowledgeLibraryPage />} />
                  {/* P2 — Enterprise Intelligence */}
                  <Route path="enterprise" element={<EnterpriseLayoutPage />}>
                    <Route index element={<Navigate to="portfolio" replace />} />
                    <Route path="portfolio" element={<EnterprisePortfolioPage />} />
                    <Route path="dependencies" element={<EnterpriseDependenciesPage />} />
                    <Route path="business-impact" element={<EnterpriseBusinessImpactPage />} />
                    <Route path="priority" element={<EnterprisePriorityPage />} />
                    <Route path="executive" element={<EnterpriseExecutivePage />} />
                    <Route path="strategy" element={<EnterpriseStrategyPage />} />
                  </Route>
                  <Route path="modernization" element={<Navigate to="operate/test-environment" replace />} />
                  {/*
                    releases / insights / portfolio / knowledge-library / enterprise
                    are live routes declared above. A second set of `<Navigate>`
                    declarations for the same paths used to sit here, labelled
                    "removed platform sidebar surfaces"; React Router resolves
                    the first declaration, so those redirects could never
                    match and the pages were live all along. The dead
                    declarations are gone; the pages are intentionally kept.
                  */}

                  {/* Operate — grouped workflow navigation */}
                  <Route path="operate" element={<OperateLayoutPage />}>
                    <Route index element={<Navigate to="incidents" replace />} />
                    <Route path="overview" element={<Navigate to="../incidents" replace />} />
                    {/* Incidents domain (replaces Mission Control). Legacy missions → redirect. */}
                    <Route path="incidents/:incidentId/*" element={<LegacyIncidentQueryRedirect />} />
                    <Route path="incidents" element={<IncidentsDomainPage />} />
                    <Route path="external-changes" element={<Navigate to="../incidents" replace />} />
                    <Route path="runtime-logs" element={<RuntimeLogsDomainPage />} />
                    {/* list · reported/new · reported/:incidentId — one route so the
                        page (and its draft form) stays mounted across views.
                        Legacy ?reported= links are redirected by the page. */}
                    <Route path="reported/*" element={<ReportedIncidentsPage />} />
                    <Route path="requests" element={<OperateRequestsPage />} />
                    <Route path="missions" element={<Navigate to="../incidents" replace />} />
                    <Route path="incident" element={<IncidentTabRedirect />} />
                    {/* Investigations tab removed — investigation lives inside Incidents */}
                    <Route path="investigations" element={<IncidentTabRedirect />} />
                    <Route path="executions" element={<ExecutionsPage />} />
                    <Route path="executors" element={<ExecutorCenterPage />} />
                    <Route path="replay" element={<CertificationPage />} />
                    <Route path="certification" element={<Navigate to="../replay" replace />} />
                    <Route path="test-environment" element={<OperateTestEnvironmentPage />} />
                    <Route path="testing" element={<OperateTabAliasRedirect tab="test-environment" />} />
                    <Route path="engineering" element={<OperateFixesPage />} />
                    {/* Legacy Fixes URL → Engineering workspace */}
                    <Route path="fixes" element={<OperateFixesPage />} />
                    <Route path="learning" element={<OperateLearningPage />} />
                    <Route path="timeline" element={<Navigate to="../incidents" replace />} />
                  </Route>

                  {/* Timeline/Activity are not standalone — entity timelines live on each surface */}
                  <Route path="activity" element={<Navigate to="operate/incidents" replace />} />

                  <Route path="ams">
                    <Route index element={<Navigate to="../operate/incidents" replace />} />
                    <Route path="*" element={<Navigate to="../operate/incidents" replace />} />
                  </Route>

                  {/* Soft aliases */}
                  <Route path="overview" element={<Navigate to=".." replace />} />
                  <Route path="operations" element={<Navigate to="../operate/incidents" replace />} />
                  <Route path="incidents" element={<Navigate to="../operate/incidents" replace />} />
                  <Route path="engineering" element={<Navigate to="../operate/engineering" replace />} />
                  <Route path="quality" element={<Navigate to="../operate/test-environment" replace />} />
                  <Route path="mission-control" element={<Navigate to="../operate/incidents" replace />} />
                  <Route path="knowledge" element={<Navigate to="../phase-1/knowledge" replace />} />
                  <Route path="settings" element={<Navigate to="/settings" replace />} />
                </Route>

                <Route path="/projects/:projectId/ingestion" element={<ProjectIngestionPage />} />
                <Route path="/projects/:projectId/artifacts" element={<ProjectLegacyArtifactsPage />} />
                <Route path="/projects/:projectId/report" element={<ProjectLegacyReportPage />} />
                <Route path="/projects/:projectId/handoff" element={<ProjectLegacyHandoffPage />} />
                <Route path="/projects/all" element={<ProjectsPage initialTab={0} />} />
                <Route path="/projects/active" element={<ProjectsPage initialTab={1} />} />
                <Route path="/projects/completed" element={<Navigate to="/projects/active" replace />} />

                <Route path="/report" element={<Phase0ReportPage />} />
                <Route path="/report/phase0" element={<Phase0ReportPage />} />
                <Route path="/handoff" element={<Phase0HandoffPage />} />
                <Route path="/phase0/handoff" element={<Phase0HandoffPage />} />

                {/* Settings tab indexes: see SETTINGS_TABS in components/settings/SettingsView.tsx */}
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/profile" element={<SettingsPage initialTab={0} />} />
                <Route path="/settings/appearance" element={<SettingsPage initialTab={1} />} />
                <Route path="/settings/security" element={<SettingsPage initialTab={2} />} />
                <Route path="/settings/notifications" element={<SettingsPage initialTab={3} />} />
                {/* API keys live on the Security tab (2), Git connections on Integrations (4). */}
                <Route path="/settings/api-keys" element={<SettingsPage initialTab={2} />} />
                <Route path="/settings/integrations" element={<SettingsPage initialTab={4} />} />
                <Route path="/settings/monitoring" element={<SettingsPage initialTab={5} />} />
                <Route path="/settings/advanced" element={<Navigate to="/settings" replace />} />

                <Route path="/notifications" element={<SettingsPage initialTab={3} />} />

                {/* /help/* placeholder pages had no content behind them and were removed. */}
                <Route path="/help/*" element={<Navigate to="/overview" replace />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
            </ErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
